import React, { useEffect } from 'react';

// Redux
import { useSelector, useDispatch } from 'react-redux';

// Material
import Box from '@material-ui/core/Box';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import ListItemText from '@material-ui/core/ListItemText';
import Checkbox from '@material-ui/core/Checkbox';
import Radio from '@material-ui/core/Radio';

// Clsx
import clsx from 'clsx';

// Actions
import * as ProfilesActions from 'store/ducks/profiles/actions';
import * as ReportActions from 'store/ducks/reports/actions';

// Types
import { ProfileInterface } from 'store/ducks/profiles/types';
import { ApplicationState } from 'store';

// Helpers
import { useDebouncedSearch } from 'helpers/debounceHook';
import { promiseDispatch } from 'store/utils/promiseAction';
import {
  getCurrentSearchTerm,
  setCurrentSearchTerm,
} from 'helpers/tableFilters';

// Components
import { SearchAsyncComponent } from 'components/SearchAsyncComponent';

// Styles
import useStyles from './profileFilter.styles';

const storegeLimit =
  JSON.parse(window.localStorage.getItem('persist:root') as string)
    ?.localLimit || '10';
const { localLimit } = JSON.parse(storegeLimit) || 10;

async function searchProfile(filter: string) {
  if (getCurrentSearchTerm() && filter.trim()) {
    return promiseDispatch(
      ProfilesActions.filterProfilesRequest({
        name: `%${filter}%`,
        _limit: localLimit,
      }),
    );
  }
  return false;
}

const useSearchProfile = () => useDebouncedSearch(searchProfile);

type Props = {
  isRadio?: boolean;
};

const ProfileFilter: React.FC<Props> = ({ isRadio }) => {
  const classes = useStyles();

  const dispatch = useDispatch();

  const [
    autoCompleteText,
    setAutoCompleteText,
    searchResults,
  ] = useSearchProfile();

  const profileList = useSelector(
    (state: ApplicationState) => state.profiles.profilesList || [],
  );

  const filteredProfiles = useSelector(
    (state: ApplicationState) => state.reports.selectedProfiles,
  );

  const filterItems = useSelector(
    (state: ApplicationState) => state.reports.filterItems,
  );

  useEffect(() => {
    dispatch(
      ProfilesActions.listProfilesRequest({
        filtering: true,
      }),
    );
  }, [dispatch]);

  const handleProfile = (profile: ProfileInterface) => {
    if (isRadio) {
      const { id, name } = profile;

      if (filteredProfiles?.find((p) => p.id === id)) return;

      dispatch(
        ReportActions.setSelectedProfiles({
          selectedProfiles: [{ id, name }],
        }),
      );

      const newData = { key: 'profile', value: name };

      dispatch(
        ReportActions.setFilteredItems({
          filterItems: filterItems
            .filter(({ key }) => key !== 'profile')
            .concat(newData),
        }),
      );
      return;
    }

    const updated = [...filteredProfiles];
    const index = filteredProfiles.findIndex((e) => e.id === profile.id);

    if (index === -1) updated.push({ id: profile.id, name: profile.name });
    else updated.splice(index, 1);

    dispatch(
      ReportActions.setSelectedProfiles({
        selectedProfiles: updated,
      }),
    );
  };

  return (
    <Box>
      <SearchAsyncComponent
        placeholder='Buscar perfil'
        loading={searchResults.loading}
        classes={{
          root: classes.root,
        }}
        inputSize='small'
        onChange={(text: string) => {
          setCurrentSearchTerm(text);
          setAutoCompleteText(text);
          if (!text) {
            dispatch(
              ProfilesActions.listProfilesRequest({
                filtering: true,
              }),
            );
          }
        }}
        value={autoCompleteText}
      />
      <Box marginTop={1}>
        <List dense>
          {Array.isArray(profileList) &&
            profileList?.length > 0 &&
            profileList?.map((item: ProfileInterface) => (
              <ListItem
                key={item.id}
                button
                className={clsx({
                  [classes.active]: !!filteredProfiles.find(
                    (e) => e.id === item.id,
                  ),
                })}>
                <ListItemText
                  id={item.id}
                  primary={item.name}
                  onClick={() => handleProfile(item)}
                />
                <ListItemSecondaryAction>
                  {isRadio ? (
                    <Radio
                      edge='end'
                      color='primary'
                      onChange={() => handleProfile(item)}
                      checked={!!filteredProfiles.find((e) => e.id === item.id)}
                      inputProps={{ 'aria-labelledby': item.id }}
                    />
                  ) : (
                    <Checkbox
                      edge='end'
                      color='primary'
                      onChange={() => handleProfile(item)}
                      checked={!!filteredProfiles.find((e) => e.id === item.id)}
                      inputProps={{ 'aria-labelledby': item.id }}
                    />
                  )}
                </ListItemSecondaryAction>
              </ListItem>
            ))}
        </List>
      </Box>
    </Box>
  );
};

export { ProfileFilter };
export default ProfileFilter;
